import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { EnhancedOrganizationForm } from "@/components/organization/EnhancedOrganizationForm";
import { OrganizationFormValues } from "@/components/organization/forms/types";
import { updateOrganization, fetchOrganizationById } from "@/services/organization/organizationService";
import { OrganizationWithRelations } from "@/types/organization.types";
import { Link } from "react-router-dom";

export default function EditOrganization() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [organization, setOrganization] = useState<OrganizationWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOrganization = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await fetchOrganizationById(id);
        setOrganization(data);
      } catch (err: any) {
        setError(err.message || "Failed to load organization");
        toast({
          title: "Error",
          description: "Failed to load organization details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganization();
  }, [id, toast]);

  const handleSubmit = async (values: OrganizationFormValues) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      
      // Convert date objects to ISO strings for database storage
      const formattedValues = {
        ...values,
        start_date: values.start_date ? new Date(values.start_date).toISOString() : null,
        contract_renewal_date: values.contract_renewal_date 
          ? new Date(values.contract_renewal_date).toISOString() 
          : null,
      };
      
      await updateOrganization(id, formattedValues);
      
      toast({
        title: "Success",
        description: "Organization updated successfully",
      });
      
      // Navigate back to organization details page
      navigate(`/organizations/${id}`);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update organization",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/organizations/${id}`}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Organization</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Organization</h1>
        </div>
        <div className="rounded-md border p-8 text-center">
          <p>Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/organizations">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Organizations</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Organization</h1>
        </div>
        <div className="rounded-md border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">{error || "Failed to load organization"}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate(`/organizations/${id}`)}
          >
            Back to Organization
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/organizations/${id}`}>
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Organization</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit {organization.name}</h1>
      </div>
      
      <EnhancedOrganizationForm 
        organization={organization}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        contacts={organization.contacts || []}
      />
    </div>
  );
}